import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  RefreshCw, 
  MessageSquare, 
  X, 
  Clock, 
  Users,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { fetchFollowups, dismissFollowup } from '../../services/dashboardService';

// Category display config
const CATEGORY_CONFIG = {
  difficult_conversation: {
    label: 'Difficult Conversation',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30'
  },
  delegation: {
    label: 'Delegation',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
  feedback: {
    label: 'Feedback',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30'
  },
  decision_making: {
    label: 'Decision Making',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  team_conflict: {
    label: 'Team Dynamics',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  }
};

const FollowupQueue = () => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  useEffect(() => {
    loadFollowups();
  }, []);

  const loadFollowups = async () => {
    try {
      const data = await fetchFollowups();
      setFollowups(data.followups || []);
    } catch (err) {
      console.error('Failed to load follow-ups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (conversationId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDismissing(conversationId);
    try {
      await dismissFollowup(conversationId);
      setFollowups(prev => prev.filter(f => f.conversation_id !== conversationId));
    } catch (err) {
      console.error('Failed to dismiss:', err);
    } finally {
      setDismissing(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <RefreshCw className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-white">Follow-up Queue</h2>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-zinc-800 rounded-lg" />
          <div className="h-20 bg-zinc-800 rounded-lg" />
        </div>
      </div>
    );
  }

  if (followups.length === 0) {
    return null; // Don't show the section if there are no follow-ups
  }

  return (
    <section 
      className="bg-gradient-to-br from-amber-500/5 to-zinc-900 border border-amber-500/20 rounded-xl p-6"
      data-testid="followup-queue"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-white">Follow-up Queue</h2>
          <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">
            {followups.length}
          </span>
        </div>
      </div>
      
      <p className="text-zinc-400 text-sm mb-4">
        Circle back on recent situations to track progress and deepen your reflections
      </p>

      <div className="space-y-3">
        {followups.map((followup) => {
          const config = CATEGORY_CONFIG[followup.situation_category] || {
            label: followup.situation_category?.replace(/_/g, ' '),
            color: 'text-zinc-400',
            bgColor: 'bg-zinc-800',
            borderColor: 'border-zinc-700'
          };

          return (
            <div
              key={followup.conversation_id}
              className={`relative p-4 rounded-lg border ${config.borderColor} ${config.bgColor} transition-all hover:border-amber-500/40`}
              data-testid={`followup-item-${followup.conversation_id}`}
            >
              {/* Dismiss button */}
              <button
                onClick={(e) => handleDismiss(followup.conversation_id, e)}
                disabled={dismissing === followup.conversation_id}
                className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/50 rounded transition-colors"
                title="Dismiss"
                data-testid={`dismiss-followup-${followup.conversation_id}`}
              >
                <X className="w-4 h-4" />
              </button>

              {/* Category & Time */}
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-medium ${config.color}`}>
                  {config.label}
                </span>
                <span className="text-zinc-600">•</span>
                <span className="text-xs text-zinc-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {followup.days_ago === 0 ? 'Today' : 
                   followup.days_ago === 1 ? 'Yesterday' : 
                   `${followup.days_ago} days ago`}
                </span>
              </div>

              {/* Suggested prompt */}
              <p className="text-white text-sm mb-3 pr-8">
                <Sparkles className="w-4 h-4 inline-block mr-1.5 text-amber-500" />
                {followup.suggested_prompt}
              </p>

              {/* People involved */}
              {followup.people_involved?.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-3 h-3 text-zinc-500" />
                  <div className="flex flex-wrap gap-1">
                    {followup.people_involved.map((person, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2 py-0.5 bg-zinc-700/50 text-zinc-300 rounded"
                      >
                        {person}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Original preview */}
              {followup.original_preview && (
                <p className="text-xs text-zinc-500 italic mb-3 line-clamp-2">
                  &ldquo;{followup.original_preview}&rdquo;
                </p>
              )}

              {/* Action button */}
              <Link
                to={`/dashboard/chat?continue=${followup.conversation_id}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-medium rounded-lg transition-colors"
                data-testid={`reflect-now-${followup.conversation_id}`}
              >
                <MessageSquare className="w-4 h-4" />
                Reflect Now
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FollowupQueue;
